import { useState } from 'react';
import { X } from 'lucide-react';
import { SimpleDialog } from '../../../components/ui/SimpleDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/Select';
import { Button } from '../../../components/ui/Button';
import Label from '../../../components/Label';
import PickClassificationModal from '../../../components/modals/PickClassificationModal';
import { useCompanyEmployees } from '../hooks/useApprovalSettings';
import { CLASSIFICATION_TREE_URL } from '../constants';

interface AddApproverDialogProps {
  open: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: { employee_id: string; classification_id: string | null }) => void;
}

const employeeName = (e: any): string =>
  e?.title || e?.name || e?.user_name || e?.full_name || e?.email || String(e?.id);

const AddApproverDialog = ({ open, onClose, onSubmit, submitting }: AddApproverDialogProps) => {
  const { data: employees } = useCompanyEmployees();
  const [employeeId, setEmployeeId] = useState<string>('');
  const [classification, setClassification] = useState<{ id: string; title: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reset = () => {
    setEmployeeId('');
    setClassification(null);
    setPickerOpen(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleOk = () => {
    if (!employeeId || submitting) return;
    onSubmit({ employee_id: employeeId, classification_id: classification?.id ?? null });
  };

  return (
    <>
      <SimpleDialog
        open={open}
        onOpenChange={(v) => !v && handleClose()}
        title="Přidat schvalovatele"
        className="w-[calc(100%-2rem)] sm:max-w-[720px]"
        onCancel={handleClose}
        onOk={handleOk}
        okText="Přidat"
        confirmLoading={submitting}
      >
        <div className="space-y-4">
          <div>
            <Label name="employee" title="Zaměstnanec" required />
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="approver-employee-select">
                <SelectValue placeholder="Vyberte zaměstnance" />
              </SelectTrigger>
              <SelectContent>
                {(employees ?? []).map((e: any) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {employeeName(e)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label name="classification" title="Klasifikace (nepovinné)" />
            {classification ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="min-w-0 break-words text-neutral-700">{classification.title}</span>
                <button
                  type="button"
                  onClick={() => setClassification(null)}
                  data-testid="approver-classification-clear"
                  className="shrink-0 text-neutral-400 hover:text-neutral-700"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="approver-classification-pick"
                  onClick={() => setPickerOpen(true)}
                >
                  Vybrat klasifikaci
                </Button>
                <span className="text-xs text-neutral-400">Bez výběru = globální schvalovatel</span>
              </div>
            )}
          </div>
        </div>
      </SimpleDialog>

      <PickClassificationModal
        visible={pickerOpen}
        url={CLASSIFICATION_TREE_URL}
        onClose={() => setPickerOpen(false)}
        onSubmit={(item: any) => {
          setClassification({ id: item.id, title: item.title });
          setPickerOpen(false);
        }}
      />
    </>
  );
};

export default AddApproverDialog;
